import { NamesOfFieldEnum } from "~/typings/types";
import { getFormattedDateForBackend, getWithPrefixPhone, sanitizeData, sanitizeURL } from "~/utils";

export interface SanitizationRules {
	[key: string]: (value: any) => any;
}

// Базовые правила очистки
const defaultSanitizationRules: SanitizationRules = {
	string: (value: string) => sanitizeData(value.trim()),
	phone: (value: string) => getWithPrefixPhone(value),
	date: (value: string) => getFormattedDateForBackend(sanitizeData(value)),
	url: (value: string) => sanitizeURL(value),
	stringArray: (value: string[]) => {
		if (!Array.isArray(value)) {
			throw new Error('Ожидался массив строк');
		}
		return value.map(item => {
			if (typeof item !== 'string') {
				throw new Error(`Элемент массива должен быть строкой, получено: ${typeof item}`);
			}
			return sanitizeData(item.trim());
		});
	},
	number: (value: number) => {
		return Number(value);
	},
	boolean: (value: boolean) => {
		if (typeof value !== 'boolean') {
			throw new Error('Ожидался boolean');
		}
		return value;
	}
};

// Конфигурация очистки для конкретных полей
const fieldSanitizationConfig: Record<string, keyof SanitizationRules> = {
	[NamesOfFieldEnum.CrmPhone]: 'phone',
	[NamesOfFieldEnum.LprPhone]: 'phone',
	[NamesOfFieldEnum.BuhPhone]: 'phone',
	[NamesOfFieldEnum.TechPhone]: 'phone',
	[NamesOfFieldEnum.PayformPhone]: 'phone',
	[NamesOfFieldEnum.CrmBirthday]: 'date',
	[NamesOfFieldEnum.LrpProxyDocDate]: 'date',
	[NamesOfFieldEnum.PayformName]: 'url',
	[NamesOfFieldEnum.PayformLinkVk]: 'url',
	[NamesOfFieldEnum.PayformLinkInstagram]: 'url',
	[NamesOfFieldEnum.PayformLinkTg]: 'url',
	[NamesOfFieldEnum.PayformLinkOther]: 'url',
	[NamesOfFieldEnum.PayformLinkSite]: 'url',
	[NamesOfFieldEnum.DocumentPricesTariffsLink]: 'url',
	[NamesOfFieldEnum.PayformPaymentMethods]: 'stringArray',
	[NamesOfFieldEnum.Partnerships]: 'stringArray',
	[NamesOfFieldEnum.LogoMainFileId]: 'number',
	[NamesOfFieldEnum.IsProdamusPayClient]: 'boolean',
	[NamesOfFieldEnum.IsOffertAccepted]: 'number',
	[NamesOfFieldEnum.IsPrivacyAccepted]: 'number',
	[NamesOfFieldEnum.IsPromoAccepted]: 'number',
	// Можно добавить другие специфические правила для полей
};

const sanitizeField = (fieldName: string, value: any, customRules?: SanitizationRules): any => {
	const rules = { ...defaultSanitizationRules, ...customRules };
	const ruleType = fieldSanitizationConfig[fieldName] || 'string';

	if (value === null || value === undefined) return value;
	return rules[ruleType] ? rules[ruleType](value) : sanitizeData(String(value).trim());
};

export const sanitizeDataObject = <T extends Record<string, any>>(
	data: T,
	customRules?: SanitizationRules
): T => {
	return Object.entries(data).reduce((acc, [key, value]) => {
		return ({
			...acc,
			[key]: sanitizeField(key, value, customRules)
		})
	}, {} as T);
};